import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useNavigate, useLocation, useParams, Navigate,
} from 'react-router';
import CheckAccess from '../../components/check-access/check-access.component';
import ContentHeader from '../../components/content-header/content-header.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import Loader from '../../components/loader/loader.component';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import PageError from '../../components/page-error/page-error.component';
import FormError from '../../components/form-error/form-error.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import LeaveConfirmModal from '../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import LimitedPageContainer from '../../components/limited-page-block/limited-page-container.component';
import PageContainer from '../../components/page-container/page-container.component';
import PageContainerSide from '../../components/page-container-side/page-container-side.component';
import PageContainerMain from '../../components/page-container-main/page-container-main.component';
import ImageContainer from '../../components/image-container/image-container.component';
import Controls from '../app/controls/controls.component';
import ActivitySummary from './activity-summary/activity-summary.component';
import ActivityComments from './activity-comments/activity-comments.component';
import ActivityValidator from './activity.validator';
import FormatService from '../../services/format-service';
import StructureService from '../../services/structure-service';
import CommentService from '../../services/comment-service';
import FormService from '../../services/form-service';
import useFormBlock from '../../hooks/use-form-block';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useEditModeQuery from '../../hooks/use-edit-mode-query';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import { toggleEditMode } from '../app/controls/controls.action';
import {
  deleteActivityComment, saveActivityComment, getActivityData, saveActivityData, saveActivityStatus, resetActivityData,
} from './activity.action';
import { deleteActivityList } from '../activity-list/activity-list.action';
import {
  getListActivityDealData, getListContactCompanyData, getListContactData, getListContactPersonData,
  getListDealAssetData, getListTagData,
} from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { FORM_ID_ACTIVITY } from '../../constants/system/form-ids';
import { ACTIVITY_EDIT, ACTIVITY_DELETE } from '../../constants/entity/permissions';
import { KEYBOARD_KEY_ARROW_UP, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_ESCAPE } from '../../constants/system/keyboard-keys';
import {
  ROUTE_ACTIVITY, ROUTE_ACTIVITY_LIST, ROUTE_CONTACT, ROUTE_SEARCH, ROUTE_PARAM_ID, ROUTE_NOT_FOUND, ROUTE_DEAL,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_TAG_ID, QUERY_PARAM_TAB, QUERY_PARAM_COMMENT_ID, QUERY_PARAM_CATEGORY,
} from '../../constants/system/query-params';
import { DATA_CONTACT_ID } from '../../constants/system/data-attributes';
import { ACTIVITY_STATUS_INITIAL } from '../../constants/entity/activity-statuses';
import { TAB_NAME_COMMENTS, TAB_NAME_SUMMARY } from '../../constants/system/tab-names';
import { HTML_TAG_NAME_ANCHOR } from '../../constants/system/html-tag-names';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import { TIMEOUT_SHORT } from '../../constants/system/timeouts';
import { ACTIVITY_CATEGORY_CAPITAL_RAISE } from '../../constants/entity/activity-categories';
import ACTIVITY_TYPE_DEBT from '../../constants/entity/activity-types';
import AssetDefaultImage from '../../static/images/asset-default-image.png';

const Activity: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COMMENT_ENTITY_TYPE_CONTACT: string = 'contact';

  const {
    isDataLoaded,
    isEditMode,
    isRightDrawerOpen,
    isCommentSaving,
    currentUser,
    activityData,
    activityStatusList,
    activityTypeList,
    activityInterestLevelList,
    dealList,
    assetList,
    tagList,
    contactList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    contactLinkedUserList,
    contactGroupList,
    measureUnitList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryCommentId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_COMMENT_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_COMMENT_ID].toString()) : null;
  const isControlHidden: boolean = !!queryTab && queryTab !== TAB_NAME_SUMMARY;
  const activityId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;
  const commentList: Comment[] = (activityData && activityData.comments) || [];

  const activityEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_EDIT], []);

  const [menuAnchor, setMenuAnchor]: [MaterialUIMenuAnchorElement | null, Function] = useState(null);
  const [isNewDealFormVisible, setIsNewDealFormVisible]: [boolean, Function] = useState(false);
  const [isNewDealSourceFormVisible, setIsNewDealSourceFormVisible]: [boolean, Function] = useState(false);
  const [isNewDealContactFormVisible, setIsNewDealContactFormVisible]: [boolean, Function] = useState(false);
  const [selectedDealContacts, setSelectedDealContacts]: [Contact | null, Function] = useState(null);
  const [selectedDealContactGroups, setSelectedDealContactGroups]: [ContactGroup | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [selectedContactCompanyContacts, setSelectedContactCompanyContacts]: [Contact[] | null, Function]
    = useState(null);
  const [selectedContactPersonContacts, setSelectedContactPersonContacts]: [Contact[] | null, Function]
    = useState(null);
  const [activityFieldItems, setActivityFieldItems]: [Record<string, ActivityFieldInput> | null, Function]
    = useState<StateValueDefault>(null);
  const [activityFieldNewItemIds, setActivityFieldNewItemIds]: [number[], Function] = useState([]);
  const [activityTags, setActivityTags]: [Tag[], Function] = useState([]);
  const [commentEditId, setCommentEditId]: [number | null, Function] = useState(null);
  const [commentText, setCommentText]: [string | null, Function] = useState(null);
  const [commentAttachments, setCommentAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentAttachmentFiles, setCommentAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentActiveId, setCommentActiveId]: [number | null, Function] = useState(null);
  const [dealPhotoFile, setDealPhotoFile]: [File | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const isFormDataPristine: boolean = isFormPristine && !activityTags.length && !dealPhotoFile;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const dealName: string = (activityData && activityData.deal && activityData.deal.name) || '';
  const pageTitle: string = `Activity${dealName ? `: ${dealName}` : ''}`;
  const deleteConfirmModalTitle: string = 'Delete Activity';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this activity?';
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));

  const onActivityDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListActivityDealData({
      query,
    }));
  }, []);

  const onActivityDealSearchClose: Function = useCallback((): void => {
    dispatch(getListActivityDealData({
      query: null,
    }));
  }, []);

  const onDealAssetSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealAssetData({
      query,
    }));
  }, []);

  const onDealAssetSearchClose: Function = useCallback((): void => {
    dispatch(getListDealAssetData({
      query: null,
    }));
  }, []);

  const onContactSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactData({
      query,
    }));
  }, []);

  const onContactSearchClose: Function = useCallback((): void => {
    dispatch(getListContactData({
      query: null,
    }));
  }, []);

  const onContactCompanySearch: Function = useCallback((query: string): void => {
    dispatch(getListContactCompanyData({
      query,
    }));
  }, []);

  const onContactCompanySearchClose: Function = useCallback((): void => {
    dispatch(getListContactCompanyData({
      query: null,
    }));
  }, []);

  const onContactPersonSearch: Function = useCallback((query: string): void => {
    dispatch(getListContactPersonData({
      query,
    }));
  }, []);

  const onContactPersonSearchClose: Function = useCallback((): void => {
    dispatch(getListContactPersonData({
      query: null,
    }));
  }, []);

  const onTagSearch: Function = useCallback((query: string): void => {
    dispatch(getListTagData({
      query,
    }));
  }, []);

  const onTagSearchClose: Function = useCallback((): void => {
    dispatch(getListTagData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setDealPhotoFile(null);
    setActivityTags([]);
    setSelectedDealContacts(null);
    setSelectedDealContactGroups(null);
    setActivityFieldNewItemIds([]);
    setIsNewDealFormVisible(false);
    setIsNewDealSourceFormVisible(false);
    setIsNewDealContactFormVisible(false);
    setSelectedContactCompanyContacts(null);
    setSelectedContactPersonContacts(null);

    dispatch(toggleEditMode(false));
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!activityId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteActivityList({
        activityIds: [activityId],
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        onCancelForm();

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_ACTIVITY_LIST,
            search: FormatService.formatQueryParams({
              [QUERY_PARAM_CATEGORY]: ACTIVITY_CATEGORY_CAPITAL_RAISE,
            }),
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [activityId]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const activityFormItem: FormValueItem = {
      ...values.activityData,
    };

    if (activityFormItem.deal && Object.keys(activityFormItem.deal).length) {
      delete activityFormItem.dealId;

      if (activityFormItem.deal && Object.keys(activityFormItem.deal).length) {
        activityFormItem.deal.mainPhoto = dealPhotoFile || null;
      }
    } else {
      delete activityFormItem.deal;

      activityFormItem.dealId = activityFormItem.dealId || null;
    }

    if (activityFormItem.dealSource && Object.keys(activityFormItem.dealSource).length) {
      delete activityFormItem.dealSourceId;
    } else {
      delete activityFormItem.dealSource;

      activityFormItem.dealSourceId = activityFormItem.dealSourceId || null;
    }

    if (activityFormItem.dealContact && Object.keys(activityFormItem.dealContact).length) {
      activityFormItem.dealContact.companyId = activityFormItem.dealContact.companyId || null;
      activityFormItem.dealContact.phone = activityFormItem.dealContact.phone
      && FormatService.isValidPhoneNumber(activityFormItem.dealContact.phone, true)
        ? FormatService.formatPhoneValue(activityFormItem.dealContact.phone) : null;
      activityFormItem.dealContact.groupIds = activityFormItem.dealContact.groupIds
      && activityFormItem.dealContact.groupIds.length
        ? activityFormItem.dealContact.groupIds.map((contactGroupItem: ContactGroup): number => contactGroupItem.id)
        : [];
    } else {
      delete activityFormItem.dealContact;
    }

    activityFormItem.sponsorId = activityFormItem.sponsorId || null;
    activityFormItem.brokerId = activityFormItem.brokerId || null;
    activityFormItem.lenderId = activityFormItem.lenderId || null;
    activityFormItem.dealContactIds = activityFormItem.dealContactIds && activityFormItem.dealContactIds.length
      ? activityFormItem.dealContactIds.map((contactItem: Contact): number => contactItem.id) : [];
    activityFormItem.amount = activityFormItem.amount
      ? FormatService.formatCleanNumber(activityFormItem.amount.toString()) : 0;
    activityFormItem.size = activityFormItem.size
      ? FormatService.formatCleanNumber(activityFormItem.size.toString()) : 0;
    activityFormItem.ltv = activityFormItem.ltv
      ? FormatService.formatCleanNumber(activityFormItem.ltv.toString(), true) : 0;
    activityFormItem.purchasePrice = activityFormItem.purchasePrice
      ? FormatService.formatCleanNumber(activityFormItem.purchasePrice.toString()) : 0;
    activityFormItem.capitalExpenditure = activityFormItem.capitalExpenditure
      ? FormatService.formatCleanNumber(activityFormItem.capitalExpenditure.toString()) : 0;
    activityFormItem.oneYearNoi = activityFormItem.oneYearNoi
      ? FormatService.formatCleanNumber(activityFormItem.oneYearNoi.toString()) : 0;
    activityFormItem.yieldOnCost = activityFormItem.yieldOnCost
      ? FormatService.formatCleanNumber(activityFormItem.yieldOnCost.toString(), true) : 0;
    activityFormItem.debtProceeds = activityFormItem.debtProceeds
      ? FormatService.formatCleanNumber(activityFormItem.debtProceeds.toString()) : 0;
    activityFormItem.requiredEquityAmount = activityFormItem.requiredEquityAmount
      ? FormatService.formatCleanNumber(activityFormItem.requiredEquityAmount.toString()) : 0;
    activityFormItem.interestLevel = activityFormItem.interestLevel || null;
    activityFormItem.ltc = activityFormItem.ltc
      ? FormatService.formatCleanNumber(activityFormItem.ltc.toString(), true) : 0;
    activityFormItem.equityProviderId = activityFormItem.equityProviderId || null;
    activityFormItem.investorId = activityFormItem.investorId || null;
    activityFormItem.additionalPartyId = activityFormItem.additionalPartyId || null;
    activityFormItem.tags = activityFormItem.tags && activityFormItem.tags.length
      ? activityFormItem.tags.map((tagItem: Tag): string => tagItem.name) : [];

    const activityOptions: ActivityFieldInput[] = [];
    if (activityFormItem.options && Object.keys(activityFormItem.options).length) {
      Object.keys(activityFormItem.options).forEach((activityFieldIndex: string): void => {
        activityOptions.push({
          ...activityFormItem.options[activityFieldIndex],
        });
      });
    }
    activityFormItem.options = activityOptions;

    const activityItem: ActivityInput = activityFormItem;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveActivityData({
        activity: activityItem,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);
        setIsMessageError(false);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, dealPhotoFile]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, dealPhotoFile, formValues, withFormErrors]);

  const openStatusMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeStatusMenu: Function = useCallback((): void => {
    setMenuAnchor(null);
  }, []);

  const onStatusSelect: Function = useCallback((statusValue: string): void => {
    activityStatusList.forEach((activityStatusItem: ActivityStatus): void => {
      if (activityStatusItem.name === statusValue) {
        dispatch(saveActivityStatus({
          activityId,
          newStatus: statusValue,
        }, (): void => {
          closeStatusMenu();

          onCancelForm();

          setStatusMessage('Saved successfully');
          setIsOpenMessage(true);
          setIsMessageError(false);
        }));
      }
    });
  }, [activityStatusList, activityId]);

  const openNewDealForm: Function = useCallback((): void => {
    changeForm('activityData.deal', {
      name: '',
    });
    setIsNewDealFormVisible(true);
  }, []);

  const closeNewDealForm: Function = useCallback((): void => {
    changeForm('activityData.deal', null);
    setIsNewDealFormVisible(false);
  }, []);

  const onDealPhotoFileChange: Function = useCallback((file: File): void => {
    setDealPhotoFile(file);
    changeForm('activityData.deal.mainPhoto', file);
  }, []);

  const onDealPhotoFileDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setDealPhotoFile(null);
    changeForm('activityData.deal.mainPhoto', null);

    event.stopPropagation();
  }, []);

  const openNewDealSourceForm: Function = useCallback((): void => {
    changeForm('activityData.dealSource', {
      name: null,
    });
    setIsNewDealSourceFormVisible(true);
  }, []);

  const closeNewDealSourceForm: Function = useCallback((): void => {
    changeForm('activityData.dealSource', null);
    setIsNewDealSourceFormVisible(false);
  }, []);

  const openNewDealContactForm: Function = useCallback((): void => {
    changeForm('activityData.dealContact', {
      firstName: null,
      lastName: null,
    });
    setIsNewDealContactFormVisible(true);
  }, []);

  const closeNewDealContactForm: Function = useCallback((): void => {
    changeForm('activityData.dealContact', null);
    setIsNewDealContactFormVisible(false);
  }, []);

  const onDealContactsChange: Function = useCallback((contactValues: Contact): void => {
    changeForm('activityData.dealContactIds', contactValues);
    setSelectedDealContacts(contactValues);
  }, []);

  const onDealContactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    changeForm('activityData.dealContact.groupIds', contactGroupValues);
    setSelectedDealContactGroups(contactGroupValues);
  }, []);

  const onDealContactPhoneChange: Function = useCallback((phoneValue?: string): void => {
    changeForm('activityData.dealContact.phone', phoneValue);
  }, []);

  const onDealClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_DEAL}/${dealId}`,
    });

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const onTagsChange: Function = useCallback((tagValues: Tag[]): void => {
    changeForm('activityData.tags', tagValues);
    setActivityTags(tagValues);
    setSelectedTags(tagValues);
  }, []);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };
    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const onActivityFieldDeleteClick: Function = useCallback((activityFieldId: number): void => {
    const formKeyActivityField: string = FormService.getIndexedFormFieldKey(activityFieldId);

    if (activityFieldItems) {
      const newActivityFieldItems: Record<string, ActivityFieldInput> = {
        ...activityFieldItems,
      };
      delete newActivityFieldItems[activityFieldId];
      setActivityFieldItems(newActivityFieldItems);
    }

    if (activityFieldNewItemIds.length) {
      const activityFieldNewItemIdIndex: number = activityFieldNewItemIds.indexOf(activityFieldId);

      if (activityFieldNewItemIdIndex !== FormatService.INDEX_NOT_FOUND) {
        StructureService.deleteListItemByIndex(activityFieldNewItemIds, activityFieldNewItemIdIndex);
      }

      setActivityFieldNewItemIds([...activityFieldNewItemIds]);
    }

    changeForm(`activityData.options[${formKeyActivityField}]`, null);
  }, [activityFieldItems, activityFieldNewItemIds]);

  const onActivityFieldAddNewItem: Function = useCallback((): void => {
    const activityFieldItem: ActivityFieldInput = {
      id: StructureService.getNewItemId([], (activityFieldItems && Object.values(activityFieldItems)) || []),
      name: '',
      value: '',
    };

    if (activityFieldItem.id) {
      setActivityFieldItems({
        ...(activityFieldItems || {}),
        [activityFieldItem.id]: activityFieldItem,
      });

      setActivityFieldNewItemIds([
        ...activityFieldNewItemIds,
        activityFieldItem.id,
      ]);
    }
  }, [activityFieldItems, activityFieldNewItemIds]);

  const switchCommentEditMode: Function = useCallback((event: React.SyntheticEvent, comment: Comment): void => {
    const isEditModeActive: boolean = !commentEditId || commentEditId !== comment.id;

    setCommentEditId(isEditModeActive ? comment.id : null);
    setCommentText(isEditModeActive ? (comment.text || '') : null);

    const commentAttachmentItems: Attachment[] | null = comment.attachments && comment.attachments.length
      ? comment.attachments : null;
    setCommentAttachments(isEditModeActive && commentAttachmentItems
      ? commentAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })) : null);
    setCommentAttachmentFiles(isEditModeActive ? [] : null);

    event.stopPropagation();
  }, [commentEditId, commentList]);

  const onSaveCommentSuccess: Function = useCallback((commentId?: number): void => {
    const lastComment: Comment | null = commentList && commentList.length ? commentList[commentList.length - 1] : null;
    const resultCommentId: number | null = commentId || (lastComment && lastComment.id) || null;

    if (resultCommentId) {
      CommentService.scrollToComment(resultCommentId);
    }
  }, [commentList]);

  const onDeleteComment: Function = useCallback((commentId: number): void => {
    dispatch(deleteActivityComment({
      activityId,
      commentId,
    }, (): void => {
      onSaveCommentSuccess();
    }));
  }, [activityId, commentList]);

  const onCancelCommentForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
    setSelectedContactCompanyContacts(null);
    setSelectedContactPersonContacts(null);
  }, []);

  const onDeleteCommentConfirm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    onDeleteComment(commentId);
    onCancelCommentForm();

    event.stopPropagation();
  }, [activityId, commentList]);

  const onDeleteCommentCancel: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (commentActiveId === commentId) {
      setCommentActiveId(null);
    }

    event.stopPropagation();
  }, [commentActiveId]);

  const onCompanyContactsChange: Function = useCallback((contactValues: Contact): void => {
    setSelectedContactCompanyContacts(contactValues);
  }, []);

  const onPersonContactsChange: Function = useCallback((contactValues: Contact): void => {
    setSelectedContactPersonContacts(contactValues);
  }, []);

  const onUpdateComment: Function = useCallback((
    comment: string, attachments: AttachmentInput[], attachmentFiles: File[], commentId: number,
    mentionContactIds: number[],
  ): void => {
    dispatch(saveActivityComment({
      comment, attachments, attachmentFiles, activityId, commentId, mentionContactIds,
    }, (): void => {
      onSaveCommentSuccess(commentId);
    }));
  }, [activityId, commentList]);

  const onAddComment: Function = useCallback((
    comment: string, attachmentFiles: File[], mentionContactIds: number[],
    linkedEntities: CommentEntityInput[],
  ): void => {
    dispatch(saveActivityComment({
      comment, attachmentFiles, activityId, mentionContactIds, linkedEntities,
    }, (): void => {
      onSaveCommentSuccess();
    }));
  }, [activityId, commentList]);

  const saveComment: Function = useCallback((): void => {
    const mentionContactIds: number[] = commentText
      ? FormatService.extractHtmlAttributes(commentText, HTML_TAG_NAME_ANCHOR, DATA_CONTACT_ID, true) as number[] : [];

    const linkedEntities: CommentEntityInput[] = [];
    if (selectedContactCompanyContacts && (selectedContactCompanyContacts as Contact[]).length) {
      (selectedContactCompanyContacts as Contact[]).forEach((contactItem: Contact): void => {
        linkedEntities.push({
          entityId: contactItem.id,
          entityType: COMMENT_ENTITY_TYPE_CONTACT,
        });
      });
    }

    if (selectedContactPersonContacts && (selectedContactPersonContacts as Contact[]).length) {
      (selectedContactPersonContacts as Contact[]).forEach((contactItem: Contact): void => {
        linkedEntities.push({
          entityId: contactItem.id,
          entityType: COMMENT_ENTITY_TYPE_CONTACT,
        });
      });
    }

    if (commentEditId) {
      if (commentText || (commentAttachments && commentAttachments.length)
        || (commentAttachmentFiles && commentAttachmentFiles.length)) {
        onUpdateComment(commentText, commentAttachments, commentAttachmentFiles, commentEditId, mentionContactIds);
      }

      setCommentEditId(null);
    } else if (commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) {
      onAddComment(commentText, commentAttachmentFiles, mentionContactIds, linkedEntities);
    }

    setCommentText(null);
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
  }, [
    commentText, commentAttachmentFiles, commentAttachments, commentEditId, selectedContactCompanyContacts,
    selectedContactPersonContacts, commentList,
  ]);

  const onCommentFieldChange: Function = useCallback((event: React.ChangeEvent, commentTextValue: string): void => {
    setCommentText(commentTextValue);
  }, []);

  const onCommentFieldKeyDown: Function = useCallback((event: React.KeyboardEvent, commentTextValue: string): void => {
    if (event.key === KEYBOARD_KEY_ARROW_UP && !commentTextValue) {
      CommentService.editLastComment();

      event.preventDefault();
    }

    if (event.key === KEYBOARD_KEY_ESCAPE) {
      onCancelCommentForm();
    }

    if (event.key === KEYBOARD_KEY_ENTER && !event.shiftKey) {
      setCommentText(commentTextValue);

      CommentService.sendComment();
    }
  }, []);

  const onCommentAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    setCommentAttachmentFiles([...(commentAttachmentFiles || []), ...files]);
  }, [commentAttachmentFiles]);

  const onCommentAttachmentFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, fileIndex: number,
  ): void => {
    const isCommentEditing: boolean = !!commentEditId;
    const currentCommentAttachmentFiles: File[] = [...(commentAttachmentFiles || [])];
    const currentCommentAttachments: AttachmentInput[] = [...(commentAttachments || [])];

    if (fileIndex !== null && fileIndex >= 0) {
      if (isCommentEditing && fileIndex < currentCommentAttachments.length) {
        const newCommentAttachments: AttachmentInput[] = currentCommentAttachments;
        StructureService.deleteListItemByIndex(newCommentAttachments, fileIndex);
        setCommentAttachments(newCommentAttachments);
      } else {
        const newCommentAttachmentFiles: File[] = currentCommentAttachmentFiles;
        StructureService.deleteListItemByIndex(
          newCommentAttachmentFiles,
          fileIndex - (isCommentEditing ? currentCommentAttachments.length : 0),
        );
        setCommentAttachmentFiles(newCommentAttachmentFiles);
      }
    }
  }, [commentAttachmentFiles, commentAttachments]);

  const onCommentAttachmentFileError: Function = useCallback((errorText: string): void => {
    setStatusMessage(errorText);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const onCommentEntityLinkClick: Function = useCallback((link: string): void => {
    navigate(link);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setIsMessageError(false);
    setStatusMessage('');
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onCommentActiveIdChange: Function = useCallback((
    event: React.ChangeEvent, commentId: number,
  ): void => {
    setCommentActiveId(commentId);

    event.stopPropagation();
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <ActivitySummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isNewDealFormVisible={isNewDealFormVisible}
          isNewDealSourceFormVisible={isNewDealSourceFormVisible}
          isNewDealContactFormVisible={isNewDealContactFormVisible}
          formError={formError}
          menuAnchor={menuAnchor}
          activityData={activityData}
          activityFieldItems={activityFieldItems}
          activityFieldNewItemIds={activityFieldNewItemIds}
          dealPhotoFile={dealPhotoFile}
          selectedTags={selectedTags}
          selectedDealContacts={selectedDealContacts}
          selectedDealContactGroups={selectedDealContactGroups}
          assetList={assetList}
          dealList={dealList}
          tagList={tagList}
          contactList={contactList}
          contactGroupList={contactGroupList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          contactPersonInternalList={contactPersonInternalList}
          measureUnitList={measureUnitList}
          activityTypeList={activityTypeList}
          activityStatusList={activityStatusList}
          activityInterestLevelList={activityInterestLevelList}
          onDealClick={onDealClick}
          onContactClick={onContactClick}
          onTagClick={onTagClick}
          onTagsChange={onTagsChange}
          onActivityFieldAddNewItem={onActivityFieldAddNewItem}
          onActivityFieldDeleteClick={onActivityFieldDeleteClick}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onStatusSelect={onStatusSelect}
          onOpenStatusMenu={openStatusMenu}
          onCloseStatusMenu={closeStatusMenu}
          onDealContactsChange={onDealContactsChange}
          onDealContactGroupsChange={onDealContactGroupsChange}
          onDealContactPhoneChange={onDealContactPhoneChange}
          onDealPhotoFileChange={onDealPhotoFileChange}
          onDealPhotoFileDelete={onDealPhotoFileDelete}
          onActivityDealSearch={onActivityDealSearch}
          onActivityDealSearchClose={onActivityDealSearchClose}
          onDealAssetSearch={onDealAssetSearch}
          onDealAssetSearchClose={onDealAssetSearchClose}
          onContactSearch={onContactSearch}
          onContactSearchClose={onContactSearchClose}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onTagSearch={onTagSearch}
          onTagSearchClose={onTagSearchClose}
          onOpenNewDealForm={openNewDealForm}
          onOpenNewDealSourceForm={openNewDealSourceForm}
          onOpenNewDealContactForm={openNewDealContactForm}
          onCloseNewDealForm={closeNewDealForm}
          onCloseNewDealSourceForm={closeNewDealSourceForm}
          onCloseNewDealContactForm={closeNewDealContactForm}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_COMMENTS,
      title: 'Comments',
      component: (
        <ActivityComments
          isCommentSaving={isCommentSaving}
          currentUser={currentUser}
          activityData={activityData}
          queryCommentId={queryCommentId}
          commentText={commentText}
          commentEditId={commentEditId}
          commentActiveId={commentActiveId}
          commentAttachments={commentAttachments}
          commentAttachmentFiles={commentAttachmentFiles}
          selectedContactCompanyContacts={selectedContactCompanyContacts}
          selectedContactPersonContacts={selectedContactPersonContacts}
          tagList={tagList}
          contactCompanyList={contactCompanyList}
          contactPersonList={contactPersonList}
          contactLinkedUserList={contactLinkedUserList}
          onSaveComment={saveComment}
          onSwitchCommentEditMode={switchCommentEditMode}
          onDeleteCommentConfirm={onDeleteCommentConfirm}
          onDeleteCommentCancel={onDeleteCommentCancel}
          onCommentFieldChange={onCommentFieldChange}
          onCommentFieldKeyDown={onCommentFieldKeyDown}
          onCommentActiveIdChange={onCommentActiveIdChange}
          onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
          onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
          onCommentAttachmentFileError={onCommentAttachmentFileError}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onContactPersonSearch={onContactPersonSearch}
          onContactPersonSearchClose={onContactPersonSearchClose}
          onCompanyContactsChange={onCompanyContactsChange}
          onPersonContactsChange={onPersonContactsChange}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_ACTIVITY}/${activityId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs, activityId]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onRefresh: Function = useCallback((): void => {
    if (activityId) {
      dispatch(getActivityData({
        activityId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [activityId]);

  const onResetActivityData: Function = useCallback((): void => {
    dispatch(resetActivityData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isDataLoaded) {
      onResetActivityData();
    }

    if (activityId) {
      dispatch(getActivityData({
        activityId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }

    return (): void => {
      onResetActivityData();
    };
  }, [activityId]);

  useEffect((): void => {
    if (activityData) {
      const initialActivityFieldItems: Record<string, ActivityField> = {};
      if (activityData.options && activityData.options.length) {
        activityData.options.forEach((activityFieldItem: ActivityField): void => {
          initialActivityFieldItems[activityFieldItem.id] = {
            ...activityFieldItem,
            id: activityFieldItem.id,
          };
        });
      }
      setActivityFieldItems(initialActivityFieldItems);

      if (!selectedTags) {
        setSelectedTags(activityData.tags || []);
      }

      if (!selectedDealContacts) {
        setSelectedDealContacts(
          activityData.dealContacts && activityData.dealContacts.length ? activityData.dealContacts : [],
        );
      }

      if (!selectedDealContactGroups) {
        setSelectedDealContactGroups([]);
      }
    }
  }, [activityData, isEditMode]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_ACTIVITY}/${activityId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!activityId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (errorMessage) {
    return (
      <PageError message={errorMessage} />
    );
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={activityEditPermissions}>
      <Form id={FORM_ID_ACTIVITY} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader editPermission={ACTIVITY_EDIT} title={pageTitle} />
          <Controls
            isHidden={isControlHidden}
            withDelete={queryTab !== TAB_NAME_COMMENTS}
            withNoEditMode={queryTab === TAB_NAME_COMMENTS}
            deletePermission={ACTIVITY_DELETE}
            editPermissions={activityEditPermissions}
            onCancelForm={onCancelForm}
            onSubmitForm={onSubmitForm}
            onDeleteClick={onDeleteClick}
          />
          <PageContainer>
            <PageContainerSide>
              <ImageContainer
                isDisabled={isEditMode}
                name={activityData.deal.name}
                image={activityData.deal.mainPhoto}
                imageThumbnail={activityData.deal.mainPhotoThumbnail}
                defaultImage={AssetDefaultImage}
              />
            </PageContainerSide>
            <PageContainerMain>
              <LimitedPageContainer isActive>
                <PageTabs
                  isDisabled={isFormChanged}
                  isCompressed={isRightDrawerOpen}
                  tabs={tabs}
                  currentTab={queryTab}
                  onTabChange={onTabChange}
                  onBeforeTabChange={onBeforeTabChange}
                />
              </LimitedPageContainer>
            </PageContainerMain>
          </PageContainer>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    activity,
  } = reducers;

  const activityData: Activity = activity.activityData && activity.activityData.activity;

  if (activityData) {
    initialValues.activityData = {
      id: activityData.id,
      status: activityData.status || ACTIVITY_STATUS_INITIAL,
      type: activityData.type || ACTIVITY_TYPE_DEBT,
      category: activityData.category,
      dealId: activityData.deal ? activityData.deal.id : null,
      dealSourceId: activityData.dealSource ? activityData.dealSource.id : null,
      sponsorId: activityData.sponsor ? activityData.sponsor.id : null,
      brokerId: activityData.broker ? activityData.broker.id : null,
      lenderId: activityData.lender ? activityData.lender.id : null,
      dealContactIds: activityData.dealContacts && activityData.dealContacts.length ? activityData.dealContacts : [],
      amount: FormatService.formatNumber(activityData.amount),
      pricing: activityData.pricing,
      size: FormatService.formatNumber(activityData.size),
      sizeType: activityData.sizeType ? activityData.sizeType.name : null,
      ltv: FormatService.formatNumber(activityData.ltv, true),
      purchasePrice: FormatService.formatNumber(activityData.purchasePrice),
      capitalExpenditure: FormatService.formatNumber(activityData.capitalExpenditure),
      oneYearNoi: FormatService.formatNumber(activityData.oneYearNoi),
      yieldOnCost: FormatService.formatNumber(activityData.yieldOnCost, true),
      debtProceeds: FormatService.formatNumber(activityData.debtProceeds),
      requiredEquityAmount: FormatService.formatNumber(activityData.requiredEquityAmount),
      interestLevel: activityData.interestLevel,
      ltc: FormatService.formatNumber(activityData.ltc, true),
      equityProviderId: activityData.equityProvider ? activityData.equityProvider.id : null,
      investorId: activityData.investor ? activityData.investor.id : null,
      additionalPartyId: activityData.additionalParty ? activityData.additionalParty.id : null,
      additionalPartyDetails: activityData.additionalPartyDetails,
      lenderRole: activityData.lenderRole,
      maxAmount: activityData.maxAmount,
      loanRecourse: activityData.loanRecourse,
      tags: activityData.tags,
      options: {},
      deal: {},
      dealSource: {},
      dealContact: {},
    };

    if (activityData.options && activityData.options.length) {
      activityData.options.forEach((activityFieldItem: ActivityField): void => {
        const formKeyActivityField: string = FormService.getIndexedFormFieldKey(activityFieldItem.id);

        initialValues.activityData.options[formKeyActivityField] = {
          name: activityFieldItem.name,
          value: activityFieldItem.value,
        };
      });
    }
  }

  return {
    isDataLoaded: !!activity.activityData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    isCommentSaving: activity.isCommentSaving,
    currentUser: app.baseData && app.baseData.currentUser,
    activityData: activity.activityData && activity.activityData.activity,
    activityStatusList: activity.activityData && activity.activityData.activityStatusList,
    dealList: activity.activityData && activity.activityData.dealList,
    contactList: activity.activityData && activity.activityData.contactList,
    contactCompanyList: activity.activityData && activity.activityData.contactCompanyList,
    contactPersonList: activity.activityData && activity.activityData.contactPersonList,
    contactPersonInternalList: activity.activityData && activity.activityData.contactPersonInternalList,
    contactLinkedUserList: activity.activityData && activity.activityData.contactLinkedUserList,
    contactGroupList: activity.activityData && activity.activityData.contactGroupList,
    measureUnitList: activity.activityData && activity.activityData.measureUnitList,
    activityInterestLevelList: activity.activityData && activity.activityData.activityInterestLevelList,
    activityTypeList: activity.activityData && activity.activityData.activityTypeList,
    assetList: activity.activityData && activity.activityData.assetList,
    tagList: activity.activityData && activity.activityData.tagList,
  };
};

const ActivityForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ activity }: Reducers): ValidationErrorsMap => (
    activity.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ activity }: Reducers): ValidationErrorsMap => (
    activity.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_ACTIVITY}
      component={Activity}
      validator={ActivityValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ActivityForm);
